// ISO 4217 currency codes and minor units; CAD is the base currency [MB D8].
import { z } from 'zod';
import { AppError } from '../kernel/errors.ts';

declare const currencyBrand: unique symbol;
export type CurrencyCode = string & { readonly [currencyBrand]: true };

export const BASE_CURRENCY = 'CAD' as CurrencyCode;

/**
 * Minor-unit exponents for the currencies KIMCO trades in (and common neighbours). A currency
 * missing here is refused rather than guessed; extend through a core change request.
 */
export const CURRENCY_MINOR_UNITS: Readonly<Record<string, number>> = Object.freeze({
  CAD: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  CHF: 2,
  AUD: 2,
  NZD: 2,
  HKD: 2,
  CNY: 2,
  INR: 2,
  BDT: 2,
  PKR: 2,
  LKR: 2,
  TRY: 2,
  MXN: 2,
  SEK: 2,
  NOK: 2,
  DKK: 2,
  PLN: 2,
  CZK: 2,
  HUF: 2,
  TWD: 2,
  THB: 2,
  MYR: 2,
  IDR: 2,
  PHP: 2,
  JPY: 0,
  KRW: 0,
  VND: 0,
});

const CODE_RE = /^[A-Z]{3}$/;

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return (
    typeof value === 'string' && CODE_RE.test(value) && Object.hasOwn(CURRENCY_MINOR_UNITS, value)
  );
}

export function toCurrency(value: string): CurrencyCode {
  if (!isCurrencyCode(value)) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [
        {
          path: 'currency',
          code: 'unknown_currency',
          messageKey: 'errors.units.unknownCurrency',
          params: { value },
        },
      ],
    });
  }
  return value;
}

export const currencyCodeSchema = z
  .string()
  .refine(isCurrencyCode, { message: 'unknown currency' })
  .transform((v) => v as CurrencyCode);

export function minorUnitsOf(currency: CurrencyCode): number {
  // toCurrency re-validates so an unbranded cast can never produce a guessed exponent.
  return CURRENCY_MINOR_UNITS[toCurrency(currency)] as number;
}
